import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import AnuncioBanner from '@/components/AnuncioBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Newspaper, Pin, RefreshCw, Globe, Clock, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

function Noticias() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newsStatus, setNewsStatus] = useState(null);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    titulo: '', contenido: '', imagen_url: '', enlace_original: ''
  });

  useEffect(() => {
    fetchNews();
    fetchStatus();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Error al cargar noticias:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/news/status`);
      setNewsStatus(response.data);
    } catch (error) {
      console.error('Error al cargar estado:', error);
    }
  };

  const handleGenerateNews = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/news/generate-manual`);
      alert(`${response.data.message}`);
      fetchNews();
      fetchStatus();
    } catch (error) {
      alert('Error al generar noticias: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/news`, formData);
      fetchNews();
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      alert('Error al crear noticia');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta noticia?')) return;
    try {
      await axios.delete(`${API}/news/${id}`);
      fetchNews();
    } catch (error) {
      console.error('Error al eliminar:', error);
    }
  };

  const handleTogglePin = async (id) => {
    try {
      await axios.put(`${API}/news/${id}/pin`);
      fetchNews();
    } catch (error) {
      alert('Error al anclar/desanclar noticia');
    }
  };

  const resetForm = () => {
    setFormData({ titulo: '', contenido: '', imagen_url: '', enlace_original: '' });
  };

  const noticiasInternas = news.filter(n => n.fuente === 'interna');
  const noticiasExternas = news.filter(n => n.fuente === 'externa');

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <AnuncioBanner />
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="noticias-title">
              Noticias y Actualidad
            </h1>
            <p className="text-gray-400">Mantente al d&iacute;a con lo que pasa en el Box y en el mundo fitness</p>
          </div>
        </div>

        {/* Banner de estado automático */}
        {newsStatus && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="news-status-banner">
            <Sparkles className="w-5 h-5 text-purple-400 shrink-0" />
            <div className="flex-1 text-sm text-gray-300">
              <span className="font-medium text-white">Noticias IA autom&aacute;ticas</span>
              {' '}&mdash; Se generan cada {newsStatus.intervalo_dias} d&iacute;as
              {newsStatus.ultima_generacion && (
                <span className="text-gray-400">
                  {' '} | &Uacute;ltima: {formatDistanceToNow(new Date(newsStatus.ultima_generacion), { addSuffix: true, locale: es })}
                </span>
              )}
              {newsStatus.proxima_generacion && (
                <span className="text-gray-400">
                  {' '} | Pr&oacute;xima: {format(new Date(newsStatus.proxima_generacion), "d MMM yyyy", { locale: es })}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {newsStatus.total_internas} internas | {newsStatus.total_externas} externas
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <Tabs defaultValue="internas" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-800 mb-8">
              <TabsTrigger value="internas" data-testid="tab-internas" className="data-[state=active]:bg-gold data-[state=active]:text-black text-gray-400">
                Noticias MK ({noticiasInternas.length})
              </TabsTrigger>
              <TabsTrigger value="externas" data-testid="tab-externas" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-gray-400">
                Actualidad Fitness ({noticiasExternas.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="internas" className="space-y-6">
              {/* Botón Crear Manual (Solo Entrenador) */}
              {user?.role === 'entrenador' && (
                <div className="flex flex-wrap justify-end gap-3 mb-4">
                  <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                  }}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary-mk" data-testid="btn-add-news">
                        <Plus className="mr-2 h-4 w-4" /> Crear Noticia Manual
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-gold">Nueva Noticia del Club</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="titulo">T&iacute;tulo</Label>
                          <Input
                            id="titulo"
                            data-testid="input-news-title"
                            value={formData.titulo}
                            onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                            required
                            className="bg-gray-800 border-gray-700 text-white"
                            placeholder="Ej: Cambio de horario Navidad"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contenido">Contenido</Label>
                          <Textarea
                            id="contenido"
                            data-testid="input-news-content"
                            value={formData.contenido}
                            onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                            required
                            className="bg-gray-800 border-gray-700 min-h-[150px]"
                            placeholder="Escribe aqu&iacute; el cuerpo de la noticia..."
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="imagen_url">URL Imagen (Opcional)</Label>
                            <Input
                              id="imagen_url"
                              value={formData.imagen_url}
                              onChange={(e) => setFormData({ ...formData, imagen_url: e.target.value })}
                              className="bg-gray-800 border-gray-700 text-white"
                              placeholder="https://..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="enlace_original">Enlace Externo (Opcional)</Label>
                            <Input
                              id="enlace_original"
                              value={formData.enlace_original}
                              onChange={(e) => setFormData({ ...formData, enlace_original: e.target.value })}
                              className="bg-gray-800 border-gray-700 text-white"
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <Button type="submit" data-testid="btn-submit-news" className="w-full btn-primary-mk mt-4">
                          Publicar
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {noticiasInternas.map((article) => (
                  <NoticiaCard 
                    key={article.id} 
                    article={article} 
                    user={user} 
                    onDelete={handleDelete} 
                    onPin={handleTogglePin} 
                  />
                ))}
                {noticiasInternas.length === 0 && (
                  <div className="col-span-full text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700">
                    <Newspaper className="w-12 h-12 mx-auto text-gray-600 mb-2" />
                    <p className="text-gray-400">No hay noticias internas publicadas.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="externas" className="space-y-6">
              {user?.role === 'entrenador' && (
                <div className="flex flex-wrap justify-end gap-3 mb-4">
                  <Button 
                    onClick={handleGenerateNews} 
                    disabled={generating}
                    data-testid="btn-generate-news"
                    className="bg-purple-600 hover:bg-purple-700 text-white" 
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${generating ? 'animate-spin' : ''}`} /> 
                    {generating ? 'Generando con IA...' : 'Regenerar Noticias IA'}
                  </Button>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {noticiasExternas.map((article) => (
                  <NoticiaCard 
                    key={article.id} 
                    article={article} 
                    user={user} 
                    onDelete={handleDelete} 
                    onPin={handleTogglePin} 
                  />
                ))}
                {noticiasExternas.length === 0 && (
                  <div className="col-span-full text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700">
                    <Globe className="w-12 h-12 mx-auto text-gray-600 mb-2" />
                    <p className="text-gray-400">No hay noticias de actualidad.</p>
                    {user?.role === 'entrenador' && (
                      <p className="text-gray-500 text-sm mt-1">Pulsa "Regenerar Noticias IA" o espera la pr&oacute;xima generaci&oacute;n autom&aacute;tica.</p>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function NoticiaCard({ article, user, onDelete, onPin }) {
  const [openFull, setOpenFull] = useState(false);
  const tipoBadge = {
    tendencia: { label: 'Tendencia', color: 'bg-blue-600' },
    consejo: { label: 'Consejo', color: 'bg-green-600' },
    estudio: { label: 'Estudio', color: 'bg-amber-600' },
    evento: { label: 'Evento MK', color: 'bg-gold text-black' },
    general: { label: '', color: '' }
  };
  const badge = tipoBadge[article.tipo] || tipoBadge.general;
  const contenidoLargo = (article.contenido || '').length > 240;

  return (
    <Card data-testid={`news-card-${article.id}`} className={`card-mk flex flex-col h-full ${article.anclada ? 'ring-2 ring-gold' : ''}`}>
      {article.imagen_url && (
        <div className="w-full h-48 overflow-hidden rounded-t-lg relative shrink-0">
          <img
            src={article.imagen_url}
            alt={article.titulo}
            className="w-full h-full object-cover transition-transform hover:scale-105"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {article.anclada && (
            <div className="absolute top-2 left-2 bg-gold text-gray-900 px-3 py-1 rounded-full font-bold flex items-center gap-1 text-xs">
              <Pin className="w-3 h-3" /> DESTACADA
            </div>
          )}
          {badge.label && (
            <div className={`absolute top-2 right-2 ${badge.color} px-2 py-0.5 rounded-full text-xs font-medium text-white`}>
              {badge.label}
            </div>
          )}
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-3 h-3 text-gray-500" />
          <p className="text-gray-500 text-xs uppercase tracking-wide">
            {format(new Date(article.fecha), "d MMMM yyyy", { locale: es })}
          </p>
          {article.fuente === 'interna' && (
            <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded-full">MK</span>
          )}
        </div>
        <CardTitle className="text-white text-lg leading-tight">
          {article.titulo}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col space-y-3 pt-0">
        {article.resumen && (
          <p className="text-gold/80 text-sm italic">{article.resumen}</p>
        )}
        <p className="text-gray-300 text-sm line-clamp-4 flex-1 whitespace-pre-line">
          {article.contenido}
        </p>
        
        <div className="mt-auto pt-3 space-y-2">
          {contenidoLargo && (
            <Button
              onClick={() => setOpenFull(true)}
              variant="ghost"
              data-testid={`btn-leer-completa-${article.id}`}
              className="w-full bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 text-sm h-9"
            >
              Leer noticia completa
            </Button>
          )}
          {article.enlace_original && (
            <a 
              href={article.enlace_original}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gold border border-gray-700 px-4 py-2 rounded transition-colors text-sm w-full"
            >
              Ir a la fuente
            </a>
          )}

          {user?.role === 'entrenador' && (
            <div className="flex gap-2 border-t border-gray-800 pt-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onPin(article.id)}
                data-testid={`btn-pin-${article.id}`}
                className={`flex-1 h-8 text-xs ${article.anclada ? 'text-gold' : 'text-gray-400 hover:text-white'}`}
              >
                <Pin className="h-3 w-3 mr-1" /> {article.anclada ? 'Desanclar' : 'Anclar'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(article.id)}
                data-testid={`btn-delete-${article.id}`}
                className="flex-1 h-8 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20"
              >
                <Trash2 className="h-3 w-3 mr-1" /> Eliminar
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      {/* Modal noticia completa */}
      <Dialog open={openFull} onOpenChange={setOpenFull}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gold/30 text-white max-h-[90vh] overflow-y-auto" data-testid={`modal-noticia-${article.id}`}>
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-3 h-3 text-gray-500" />
              <p className="text-gray-500 text-xs uppercase tracking-wide">
                {format(new Date(article.fecha), "d MMMM yyyy", { locale: es })}
              </p>
              {article.fuente === 'interna' && (
                <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded-full">MK</span>
              )}
              {badge.label && (
                <span className={`${badge.color} px-2 py-0.5 rounded-full text-[10px] font-medium text-white`}>
                  {badge.label}
                </span>
              )}
            </div>
            <DialogTitle className="text-gold text-2xl leading-tight">
              {article.titulo}
            </DialogTitle>
          </DialogHeader>
          {article.imagen_url && (
            <div className="w-full max-h-72 overflow-hidden rounded-lg my-3">
              <img
                src={article.imagen_url}
                alt={article.titulo}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          {article.resumen && (
            <p className="text-gold/80 italic">{article.resumen}</p>
          )}
          <div className="text-gray-200 leading-relaxed whitespace-pre-line text-[15px]">
            {article.contenido}
          </div>
          {article.enlace_original && (
            <a
              href={article.enlace_original}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gold border border-gray-700 px-4 py-2 rounded transition-colors text-sm self-start mt-2"
            >
              Ir a la fuente original
            </a>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default Noticias;
